<script>
    /* global IntersectionObserver */
    import BookAccordianItem from './BookAccordianItem.svelte';
    import './book-accordion.css';
    import { createEventDispatcher, onDestroy } from 'svelte';
    import { getBookAccordionDeps } from './context.js';

    const { functions } = getBookAccordionDeps();
    const callable = functions?.callable ?? null;
    /**
     * @typedef {Object} Props
     * @property {any} [items]
     * @property {any} parentID
     * @property {any} [level]
     * @property {any[]} [expectedIds]
     * @property {Map<string, Set<string>>} [hiddenIdsByCollection]
     * @property {Set<string> | null} [currentHiddenIds]
     * @property {Set<string>} [hiddenTitlesSet]
     * @property {boolean} [parentHidden]
     */
    let {
        items = [],
        parentID,
        level = null,
        expectedIds = [],
        hiddenIdsByCollection = new Map(),
        currentHiddenIds = null,
        hiddenTitlesSet = new Set(),
        parentHidden = false,
    } = $props();

    const dispatch = createEventDispatcher();
    
    let activeItem = $state(null);
    const PAGE_SIZE = 50;
    let renderCount = $state(Math.min(items.length, PAGE_SIZE));
    let loadMoreEl = $state(null);
    let loadObserver = null;
    let isCachingMissingBooks = $state(false);
    const attemptedMissingIds = new Set();
    let cacheLibraryResourcesFn = null;

    if (typeof window !== 'undefined' && callable) {
        cacheLibraryResourcesFn = callable('cacheLibraryResources');
    }

    function toggleItem(event) {
        dispatch('toggle', {
            itemId: event.detail.itemId
        });

        if (activeItem !== event.detail.itemId) {
            activeItem = event.detail.itemId;
        } else {
            activeItem = null;
        }
    }

    function setupObserver(target) {
        if (!target) return;
        if (!loadMoreEl || typeof IntersectionObserver === 'undefined') return;
        loadObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    renderCount = Math.min(renderCount + PAGE_SIZE, items.length);
                }
            });
        }, { rootMargin: '200px' });
        loadObserver.observe(loadMoreEl);
    }

    $effect(() => {
        renderCount = Math.min(items.length, PAGE_SIZE);
    });

    $effect(() => {
        const currentItems = items || [];
        if (
            cacheLibraryResourcesFn &&
            !isCachingMissingBooks &&
            expectedIds &&
            expectedIds.length
        ) {
            const missingIds = expectedIds.filter(
                (id) => !currentItems.some((item) => item?.id === id)
            );
            const idsToCache = missingIds.filter(
                (id) => !attemptedMissingIds.has(id)
            );
            if (idsToCache.length > 0) {
                idsToCache.forEach((id) => attemptedMissingIds.add(id));
                isCachingMissingBooks = true;
                cacheLibraryResourcesFor(idsToCache);
            }
        }
    });

    async function cacheLibraryResourcesFor(ids) {
        if (!cacheLibraryResourcesFn) {
            isCachingMissingBooks = false;
            dispatch('cacheComplete', { missingIds: ids });
            return;
        }

        try {
            await Promise.allSettled(
                ids.map((id) =>
                    cacheLibraryResourcesFn({ id }).catch((error) => {
                        console.error('Error cacheLibraryResources:', id, error);
                        return null;
                    })
                )
            );
        } finally {
            isCachingMissingBooks = false;
            dispatch('cacheComplete', { missingIds: ids });
        }
    }

    $effect(() => {
        if (loadObserver) loadObserver.disconnect();
        setupObserver(loadMoreEl);
    });

    onDestroy(() => {
        if (loadObserver) loadObserver.disconnect();
    });
</script>

<ul data-parentid={parentID} class="collection-section">
    {#each items.slice(0, renderCount) as data (data?.id ?? data?.docPath ?? data?.title)}
        <BookAccordianItem {data} on:toggle={toggleItem} {activeItem} {level} {hiddenIdsByCollection} {currentHiddenIds} {hiddenTitlesSet} {parentHidden} />
    {/each}
    {#if renderCount < items.length}
        <li bind:this={loadMoreEl} style="min-height: 1px;"></li>
    {/if}
</ul>
